import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Row, Col, Card, Form, Input, Select, Switch, DatePicker,
  Button, Avatar, Tag, Typography, Breadcrumb, Divider,
  Spin, Empty, Space, Segmented, Tooltip, Table, Popconfirm, message,
} from 'antd';
import {
  HomeOutlined, ArrowLeftOutlined, SearchOutlined,
  CheckCircleOutlined, ClockCircleOutlined, UserOutlined,
  PhoneOutlined, BankOutlined, SaveOutlined,
  HeartOutlined, SmileOutlined, MedicineBoxOutlined,
  EditOutlined, PlusOutlined, DeleteOutlined, DownloadOutlined, FilePdfOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { eventApi }        from '../../../api/event.api';
import { registrationApi } from '../../../api/registration.api';
import { checkupApi }      from '../../../api/checkup.api';
import { smilepassportApi } from '../../../api/smilepassport.api';
import { treatmentApi } from '../../../api/treatment.api';
import SmilepassportModal from '../../event/components/SmilepassportModal';
import SmilepassportPreviewModal from '../../event/components/SmilepassportPreviewModal';
import TreatmentModal from '../components/TreatmentModal';
import TreatmentReportPreviewModal from '../components/TreatmentReportPreviewModal';
import useDebounce         from '../../../hooks/useDebounce';
import { usePermission }   from '../../../hooks/usePermission';
import { ORAL_HYGIENE, JAW_DEVELOPMENT, JAW_WIDTH, TONGUE_MOVEMENT, MOUTH_BREATHING } from '../../../utils/constants';
import { resolveFileUrl } from '../../../utils/helpers';

const { Title, Text } = Typography;

const calcAge = (dob) => {
  if (!dob) return null;
  return dayjs().diff(dayjs(dob), 'year');
};

// ── Sidebar child item ────────────────────────────────────────────
const STANDARD_OPTIONS = [
  'Nursery', 'LKG', 'UKG',
  '1st', '2nd', '3rd', '4th', '5th',
  '6th', '7th', '8th', '9th', '10th',
  '11th', '12th',
];

const AVATAR_PALETTE = ['var(--wl-primary-light)', 'var(--wl-primary)', '#00897b', '#1e88e5', '#e53935', '#fb8c00', '#43a047', '#6d4c41'];
const avatarColor = (name) => AVATAR_PALETTE[(name?.charCodeAt(0) || 65) % AVATAR_PALETTE.length];

const ChildListItem = ({ reg, active, hasCheckup, onClick, isSchoolStaff }) => {
  const childImage = reg.child?.childImage;
  return (
  <div
    onClick={onClick}
    style={{
      display:      'flex',
      alignItems:   'center',
      gap:          10,
      padding:      '8px 12px',
      borderRadius: 10,
      cursor:       'pointer',
      background:   active ? 'var(--wl-primary-pale)' : 'transparent',
      border:       active ? '1.5px solid var(--wl-primary-light)' : '1.5px solid transparent',
      transition:   'all 0.15s',
      marginBottom: 4,
    }}
  >
    <Avatar
      size={34}
      src={childImage ? resolveFileUrl(childImage) : undefined}
      style={{
        flexShrink: 0,
        background: childImage ? undefined : avatarColor(reg.child?.name),
        color:      '#fff',
        fontWeight: 700,
        fontSize:   14,
      }}
    >
      {!childImage && reg.child?.name?.charAt(0)?.toUpperCase()}
    </Avatar>
    <div style={{ flex: 1, minWidth: 0 }}>
      <Text strong ellipsis style={{ fontSize: 13, color: active ? 'var(--wl-primary)' : 'var(--wl-text)', display: 'block' }}>
        {reg.child?.name}
      </Text>
      {isSchoolStaff && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {reg.child?.parent?.phone || '—'}
        </Text>
      )}
    </div>
    <Tooltip title={hasCheckup ? 'Checked' : 'Pending'}>
      {hasCheckup
        ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 15 }} />
        : <ClockCircleOutlined style={{ color: '#fa8c16', fontSize: 15 }} />}
    </Tooltip>
  </div>
  );
};

// ── Smile Passport Tab ────────────────────────────────────────────
const SmilePassportTab = ({
  child, checkup, isDoctor, isAdmin,
  onIssue, onUpdatePassport, onRevoke,
  issueLoading, revokeLoading,
}) => {
  const [passportForm] = Form.useForm();
  const [formVisible,  setFormVisible]  = useState(false);
  const [editMode,     setEditMode]     = useState(false);

  const hasPassport = !!checkup?.smilePassportIssued;

  useEffect(() => {
    if (!formVisible) { passportForm.resetFields(); return; }
    if (editMode && checkup) {
      passportForm.setFieldsValue({ passportNotes: checkup.passportNotes });
    }
  }, [formVisible, editMode, checkup, passportForm]);

  const openCreate = () => { setEditMode(false); setFormVisible(true); };
  const openEdit   = () => { setEditMode(true);  setFormVisible(true); };

  const handleSubmit = async () => {
    const values = await passportForm.validateFields().catch(() => null);
    if (!values) return;
    const handler = editMode ? onUpdatePassport : onIssue;
    handler(values, () => setFormVisible(false));
  };

  // if (!checkup) {
  //   return (
  //     <div style={{ textAlign: 'center', padding: '60px 20px' }}>
  //       <SmileOutlined style={{ fontSize: 40, color: 'var(--wl-primary-light)', marginBottom: 12 }} />
  //       <div style={{ color: '#666', fontSize: 14, marginBottom: 4 }}>No checkup recorded yet</div>
  //       <div style={{ color: 'var(--wl-text-muted)', fontSize: 12 }}>Record a checkup first before issuing a smile passport</div>
  //     </div>
  //   );
  // }

  // ── Form view ────────────────────────────────────────────────
  if (formVisible) {
    return (
      <Card
        title={
          <Space>
            <SmileOutlined style={{ color: 'var(--wl-primary-light)' }} />
            <span style={{ fontFamily: "'Fredoka One', sans-serif", color: 'var(--wl-primary)' }}>
              {editMode ? 'Update Passport' : 'Issue Smile Passport'}
            </span>
          </Space>
        }
        extra={<Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setFormVisible(false)}>Back to List</Button>}
        style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}
      >
        <Form form={passportForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Issued To">
                <Input value={child?.name} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Issue Date">
                <Input value={dayjs().format('DD MMM YYYY')} disabled />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="passportNotes" label="Notes">
            <Input.TextArea rows={3} placeholder="Any notes about the passport…" />
          </Form.Item>
        </Form>
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={issueLoading}
            onClick={handleSubmit}
            style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none', borderRadius: 9999 }}
          >
            {editMode ? 'Update' : 'Issue Passport'}
          </Button>
          <Button onClick={() => setFormVisible(false)}>Cancel</Button>
        </div>
      </Card>
    );
  }

  // ── List view ────────────────────────────────────────────────
  const passportRows = hasPassport ? [{
    key:       '1',
    sno:       1,
    issueDate: checkup?.updatedAt ? dayjs(checkup.updatedAt).format('DD MMM YYYY') : '—',
    childName: child?.name  || '—',
    issuedBy:  checkup?.doctor?.name || '—',
    status:    'Issued',
  }] : [];

  const columns = [
    { title: 'S.No',       dataIndex: 'sno',       width: 70 },
    { title: 'Issue Date', dataIndex: 'issueDate' },
    { title: 'Child Name', dataIndex: 'childName' },
    { title: 'Issued By',  dataIndex: 'issuedBy'  },
    {
      title:     'Status',
      dataIndex: 'status',
      render:    (s) => <Tag color="success" style={{ borderRadius: 9999 }}>{s}</Tag>,
    },
    ...(isDoctor ? [{
      title:  'Actions',
      key:    'actions',
      render: () => (
        <Space size={8}>
          {isAdmin && (
            <Button
              shape="circle" size="small"
              icon={<DownloadOutlined />}
              style={{ background: '#ffffff', border: '1px solid var(--wl-primary)', color: 'var(--wl-primary)' }}
            />
          )}
          <Button
            shape="circle" size="small"
            icon={<EditOutlined />}
            onClick={openEdit}
            style={{ background: '#ffffff', border: '1px solid var(--wl-primary)', color: 'var(--wl-primary)' }}
          />
          <Popconfirm
            title="Revoke this smile passport?"
            onConfirm={onRevoke}
            okText="Revoke"
            okButtonProps={{ danger: true }}
            placement="topRight"
          >
            <Button
              shape="circle" size="small"
              icon={<DeleteOutlined />}
              danger
              loading={revokeLoading}
              style={{ background: '#fff2f0', border: '1px solid #ff4d4f', color: '#ff4d4f' }}
            />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>

    </Space>
  );
};

// ── Treatment Details Tab ─────────────────────────────────────────
const TreatmentDetailsTab = ({
  checkup, isDoctor, childId,
  treatmentRecords, treatmentLoading,
  onAddTreatment, onEditTreatment, onDeleteTreatment, deleteLoading,
  onPreviewReport,
}) => {
  if (!checkup) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <MedicineBoxOutlined style={{ fontSize: 40, color: 'var(--wl-primary-light)', marginBottom: 12 }} />
        <div style={{ color: '#666', fontSize: 14, marginBottom: 4 }}>No treatment records found</div>
        <div style={{ color: 'var(--wl-text-muted)', fontSize: 12 }}>Record a checkup first to add treatment details</div>
      </div>
    );
  }

  return (
    <Card
      title={
        <Space>
          <MedicineBoxOutlined style={{ color: 'var(--wl-primary)' }} />
          <span style={{ fontFamily: "'Fredoka One', sans-serif", color: 'var(--wl-primary)' }}>Treatment Records</span>
        </Space>
      }
      extra={
        isDoctor ? (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={onAddTreatment}
            disabled={!childId}
            style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none', borderRadius: 9999 }}
          >
            Add Treatment
          </Button>
        ) : null
      }
      style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}
      styles={{ body: { padding: 0 } }}
    >
      <Table
        dataSource={treatmentRecords}
        rowKey={(record) => record.id || record._id}
        loading={treatmentLoading}
        pagination={false}
        scroll={{ x: 900 }}
        columns={[
          { title: '#', width: 50, render: (_, __, index) => index + 1 },
          { title: 'Treatment ID', width: 120, render: (_, r) => r.treatmentId ? <Tag style={{ borderRadius: 9999, background: 'var(--wl-primary-pale)', color: 'var(--wl-primary)', border: '1px solid var(--wl-primary-light)', fontWeight: 600 }}>{r.treatmentId}</Tag> : '—' },
          { title: 'Event', width: 180, render: (_, r) => r.event?.title || '—' },
          {
            title: 'Status',
            width: 100,
            render: (_, r) => (
              <Tag color={r.status === 'published' ? 'success' : 'default'} style={{ borderRadius: 9999, textTransform: 'capitalize' }}>
                {r.status || 'draft'}
              </Tag>
            ),
          },
          { title: 'Visit Date', width: 110, render: (_, r) => r.recordedAt ? dayjs(r.recordedAt).format('DD MMM YYYY') : '—' },
          {
            title: 'Report',
            width: 150,
            align: 'center',
            render: (_, r) => r.reportUrl ? (
              <Space size={4}>
                <Tooltip title="Preview Report">
                  <Button
                    type="text" size="small" icon={<FilePdfOutlined />}
                    onClick={() => onPreviewReport(r)}
                  />
                </Tooltip>
                {r.reportId && <Tag color="blue" style={{ borderRadius: 9999 }}>{r.reportId}</Tag>}
              </Space>
            ) : <Text type="secondary">—</Text>,
          },
          ...(isDoctor ? [{
            title: 'Actions',
            key: 'actions',
            width: 110,
            align: 'center',
            render: (_, record) => (
              <Space size={6}>
                <Tooltip title="Edit Record">
                  <Button shape="circle" size="small" icon={<EditOutlined />} style={{ background: '#ffffff', border: '1px solid var(--wl-primary)', color: 'var(--wl-primary)' }} onClick={() => onEditTreatment(record)} />
                </Tooltip>
                <Popconfirm
                  title="Delete this treatment record?"
                  onConfirm={() => onDeleteTreatment(record.id || record._id)}
                  okText="Delete"
                  okType="danger"
                  cancelText="Cancel"
                >
                  <Tooltip title="Delete Record">
                    <Button shape="circle" size="small" danger icon={<DeleteOutlined />} loading={deleteLoading} style={{ background: '#fff2f0', border: '1px solid #ff4d4f', color: '#ff4d4f' }} />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          }] : []),
        ]}
        locale={{
          emptyText: (
            <div style={{ padding: '48px 20px', textAlign: 'center' }}>
              <MedicineBoxOutlined style={{ fontSize: 40, color: 'var(--wl-primary-light)', marginBottom: 12, display: 'block' }} />
              <Text style={{ color: '#666', fontSize: 14, display: 'block', marginBottom: 4 }}>No treatment recorded</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {isDoctor ? 'Click "Add Treatment" to record treatment details' : 'No treatment has been recorded'}
              </Text>
            </div>
          ),
        }}
      />
    </Card>
  );
};

// ── Main page ─────────────────────────────────────────────────────
const ChildDetailsPage = () => {
  const { eventId, registrationId } = useParams();
  const [searchParams] = useSearchParams();
  const fromChild = searchParams.get('from') === 'child';
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const { isDoctor, isAdmin, isSchoolStaff } = usePermission();

  const [form]                  = Form.useForm();
  const [childSearch,             setChildSearch]            = useState('');
  const [listFilter,              setListFilter]             = useState('all'); // 'all' | 'pending' | 'checked'
  const [standardFilter,          setStandardFilter]         = useState('all');
  const [sectionFilter,           setSectionFilter]          = useState('all');
  const [activeTab,               setActiveTab]              = useState('checkup');
  const [checkupFormVisible,      setCheckupFormVisible]     = useState(false);
  const [passportModalOpen,       setPassportModalOpen]      = useState(false);
  const [passportEditingRecord,   setPassportEditingRecord]  = useState(null);
  const [passportPreviewOpen,     setPassportPreviewOpen]    = useState(false);
  const [passportPreviewRecord,   setPassportPreviewRecord]  = useState(null);
  const [treatmentModalOpen,      setTreatmentModalOpen]     = useState(false);
  const [treatmentEditingRecord,  setTreatmentEditingRecord] = useState(null);
  const [reportPreviewOpen,       setReportPreviewOpen]      = useState(false);
  const [reportPreviewRecord,     setReportPreviewRecord]    = useState(null);

  const debouncedSearch = useDebounce(childSearch, 250);

  // ── Queries ───────────────────────────────────────────────────
  const { data: eventData, isLoading: eventLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn:  () => eventApi.getById(eventId),
  });
  const { data: regData, isLoading: regLoading } = useQuery({
    queryKey: ['event-registrations', eventId],
    queryFn:  () => registrationApi.getByEvent(eventId),
  });
  const { data: checkupsData } = useQuery({
    queryKey: ['event-checkups', eventId],
    queryFn:  () => checkupApi.getAll({ event: eventId, limit: 500 }),
  });

  // ── Derived ───────────────────────────────────────────────────
  const event            = eventData?.data;
  const allRegistrations = regData?.data || [];

  const checkupByChildId = useMemo(() => {
    const map = {};
    (checkupsData?.data || []).forEach((c) => {
      const cid = c.child?.id || c.child?._id || String(c.child || '');
      if (cid) map[cid] = c;
    });
    return map;
  }, [checkupsData]);

  const checkedCount = useMemo(
    () => allRegistrations.filter(r => !!checkupByChildId[String(r.child?.id || r.child?._id || '')]).length,
    [allRegistrations, checkupByChildId]
  );
  const pendingCount = allRegistrations.length - checkedCount;

  // Unique standards present in this event's registrations
  const standardOptions = useMemo(() => {
    const seen = new Set();
    allRegistrations.forEach(r => { if (r.child?.standard) seen.add(r.child.standard); });
    return [...seen].sort();
  }, [allRegistrations]);

  // Sections available for the selected standard
  const sectionOptions = useMemo(() => {
    const seen = new Set();
    allRegistrations.forEach(r => {
      const matchesStandard = standardFilter === 'all' || r.child?.standard === standardFilter;
      if (matchesStandard && r.child?.section) seen.add(r.child.section);
    });
    return [...seen].sort();
  }, [allRegistrations, standardFilter]);

  const filteredRegs = useMemo(() => {
    let rows = allRegistrations;
    if (listFilter === 'pending') rows = rows.filter(r => !checkupByChildId[String(r.child?.id || r.child?._id || '')]);
    if (listFilter === 'checked') rows = rows.filter(r => !!checkupByChildId[String(r.child?.id || r.child?._id || '')]);
    if (standardFilter && standardFilter !== 'all') rows = rows.filter(r => r.child?.standard === standardFilter);
    if (sectionFilter && sectionFilter !== 'all')  rows = rows.filter(r => r.child?.section  === sectionFilter);
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      rows = rows.filter((r) =>
        r.child?.name?.toLowerCase().includes(q) ||
        String(r.child?.parent?.phone ?? '').includes(q)
      );
    }
    return [...rows].sort((a, b) => (a.child?.name || '').localeCompare(b.child?.name || ''));
  }, [allRegistrations, debouncedSearch, listFilter, checkupByChildId, standardFilter, sectionFilter]);

  const currentReg      = allRegistrations.find((r) => (r.id || r._id) === registrationId);
  const child           = currentReg?.child;
  const childId         = child?.id || child?._id;
  const existingCheckup = childId ? checkupByChildId[String(childId)] : null;
  const isEdit          = !!existingCheckup;

  
  const { data: passportRecords, isLoading: passportLoading } = useQuery({
    queryKey: ['event-smilepassports', eventId, childId],
    queryFn:  () => childId ? smilepassportApi.getByEventAndChild(eventId, childId) : Promise.resolve([]),
    enabled: !!childId,
    select:  (data) => data?.data || [],
  });

  const { data: treatmentRecords, isLoading: treatmentLoading } = useQuery({
    queryKey: ['event-treatments', eventId, childId],
    queryFn:  () => treatmentApi.getByEventAndChild(eventId, childId),
    enabled: !!childId,
    select:  (data) => data?.data || [],
  });


  // Reset form view when child changes
  useEffect(() => {
    setCheckupFormVisible(false);
  }, [registrationId]);

  // Populate / reset checkup form
  useEffect(() => {
    if (existingCheckup) {
      form.setFieldsValue({
        oralHygieneStatus: existingCheckup.oralHygieneStatus,
        weight:            existingCheckup.weight,
        height:            existingCheckup.height,
        hasCavities:       existingCheckup.hasCavities       ?? false,
        hasGumIssues:      existingCheckup.hasGumIssues      ?? false,
        painReported:      existingCheckup.painReported      ?? false,
        treatmentRequired: existingCheckup.treatmentRequired ?? false,
        jawDevelopment:    existingCheckup.jawDevelopment,
        upperJaw:          existingCheckup.upperJaw,
        lowerArch:         existingCheckup.lowerArch,
        tongueMovement:    existingCheckup.tongueMovement,
        mouthBreathing:    existingCheckup.mouthBreathing    ?? false,
        treatmentType:  existingCheckup.treatmentType,
        nextVisitDate:  existingCheckup.nextVisitDate ? dayjs(existingCheckup.nextVisitDate) : null,
        treatmentNotes: existingCheckup.treatmentNotes,
        doctorRemarks:  existingCheckup.doctorRemarks,
      });
    } else {
      form.resetFields();
    }
  }, [registrationId, existingCheckup, form]);

  // ── Shared invalidate ────────────────────────────────────────
  const invalidate = () => {
    queryClient.invalidateQueries(['event-registrations', eventId]);
    queryClient.invalidateQueries(['event-checkups',      eventId]);
    queryClient.invalidateQueries(['event-checkup-stats', eventId]);
    queryClient.invalidateQueries(['event',               eventId]);
  };

  // ── Checkup CRUD mutations ───────────────────────────────────
  const createCheckupMutation = useMutation({
    mutationFn: (data) => checkupApi.create(data),
    onError:    (err) => message.error(err.message),
  });
  const updateCheckupMutation = useMutation({
    mutationFn: ({ id, data }) => checkupApi.update(id, data),
    onError:    (err) => message.error(err.message),
  });
  const deleteCheckupMutation = useMutation({
    mutationFn: (id) => checkupApi.delete(id),
    onSuccess:  () => { invalidate(); message.success('Checkup deleted'); },
    onError:    (err) => message.error(err.message),
  });

  // ── Passport mutations ───────────────────────────────────────
  const issuePassportMutation = useMutation({
    mutationFn: (data) => checkupApi.update(existingCheckup.id || existingCheckup._id, { smilePassportIssued: true, ...data }),
    onSuccess:  () => { invalidate(); message.success('Smile passport Published'); },
    onError:    (err) => message.error(err.message),
  });
  const revokePassportMutation = useMutation({
    mutationFn: () => checkupApi.update(existingCheckup.id || existingCheckup._id, { smilePassportIssued: false }),
    onSuccess:  () => { invalidate(); message.success('Smile passport revoked'); },
    onError:    (err) => message.error(err.message),
  });
  const deletePassportRecordMutation = useMutation({
    mutationFn: (id) => smilepassportApi.delete(id),
    onSuccess:  () => { queryClient.invalidateQueries(['event-smilepassports', eventId, childId]); message.success('Record deleted'); },
    onError:    (err) => message.error(err.message),
  });

  // ── Treatment mutations ──────────────────────────────────────
  const deleteTreatmentMutation = useMutation({
    mutationFn: (id) => treatmentApi.delete(id),
    onSuccess:  () => { queryClient.invalidateQueries(['event-treatments', eventId, childId]); message.success('Treatment record deleted'); },
    onError:    (err) => message.error(err.message),
  });

  const isSubmitting = createCheckupMutation.isPending || updateCheckupMutation.isPending;

  // ── Checkup form submit ──────────────────────────────────────
  const afterCheckupSave = () => {
    invalidate();
    message.success(isEdit ? 'Checkup updated' : 'Checkup saved');
    setCheckupFormVisible(false);
  };

  const handleCheckupSubmit = async () => {
    let values;
    try { values = await form.validateFields(); }
    catch { return; }

    const payload = {
      ...values,
      weight:        values.weight ? parseFloat(values.weight) : undefined,
      height:        values.height ? parseFloat(values.height) : undefined,
      nextVisitDate: values.nextVisitDate ? values.nextVisitDate.format('YYYY-MM-DD') : undefined,
      event:        eventId,
      child:        childId,
      registration: currentReg?.id || currentReg?._id,
    };

    if (isEdit) {
      updateCheckupMutation.mutate(
        { id: existingCheckup.id || existingCheckup._id, data: payload },
        { onSuccess: afterCheckupSave }
      );
    } else {
      createCheckupMutation.mutate(payload, { onSuccess: afterCheckupSave });
    }
  };

  if (eventLoading || regLoading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  // ── Checkup tab: list view ────────────────────────────────────
  const checkupListColumns = [
    { title: 'S.No', key: 'sno', width: 60, render: (_, __, i) => i + 1 },
    { title: 'Date', dataIndex: 'createdAt', render: (d) => d ? dayjs(d).format('DD MMM YYYY') : '—' },
    {
      title:     'Oral Hygiene',
      dataIndex: 'oralHygieneStatus',
      render:    (v) => v ? (
        <Tag color={v === 'good' ? 'success' : v === 'fair' ? 'warning' : 'error'} style={{ borderRadius: 9999, textTransform: 'capitalize' }}>
          {v}
        </Tag>
      ) : '—',
    },
    {
      title:     'Cavities',
      dataIndex: 'hasCavities',
      render:    (v) => v ? <Tag color="error" style={{ borderRadius: 9999 }}>Yes</Tag> : <Tag color="success" style={{ borderRadius: 9999 }}>No</Tag>,
    },
    {
      title:     'Gum Issues',
      dataIndex: 'hasGumIssues',
      render:    (v) => v ? <Tag color="error" style={{ borderRadius: 9999 }}>Yes</Tag> : <Tag color="success" style={{ borderRadius: 9999 }}>No</Tag>,
    },
    {
      title:     'Treatment',
      dataIndex: 'treatmentRequired',
      render:    (v) => v ? <Tag color="warning" style={{ borderRadius: 9999 }}>Needed</Tag> : <Tag color="success" style={{ borderRadius: 9999 }}>Not Required</Tag>,
    },
    { title: 'Checked By', dataIndex: 'doctor', render: (v) => v?.name || '—' },
    ...(isDoctor ? [{
      title:  'Actions',
      key:    'actions',
      render: (_, r) => (
        <Space size={8}>
          <Button
            shape="circle" size="small"
            icon={<EditOutlined />}
            onClick={() => setCheckupFormVisible(true)}
            style={{ background: '#ffffff', border: '1px solid var(--wl-primary)', color: 'var(--wl-primary)' }}
          />
          <Popconfirm
            title="Delete this checkup record?"
            onConfirm={() => deleteCheckupMutation.mutate(r.id || r._id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
            placement="topRight"
          >
            <Button
              shape="circle" size="small"
              icon={<DeleteOutlined />}
              danger
              loading={deleteCheckupMutation.isPending}
              style={{ background: '#fff2f0', border: '1px solid #ff4d4f', color: '#ff4d4f' }}
            />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  const checkupListView = (
    <Card
      title={
        <Space>
          <HeartOutlined style={{ color: '#ff4d4f' }} />
          <span style={{ fontFamily: "'Fredoka One', sans-serif", color: 'var(--wl-primary)' }}>Checkup Records</span>
        </Space>
      }
      extra={
        isDoctor && !existingCheckup ? (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setCheckupFormVisible(true)}
            style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none', borderRadius: 9999 }}
          >
            New Checkup
          </Button>
        ) : null
      }
      style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}
      styles={{ body: { padding: 0 } }}
    >
      <Table
        dataSource={existingCheckup ? [existingCheckup] : []}
        columns={checkupListColumns}
        rowKey={(r) => r.id || r._id}
        pagination={false}
        scroll={{ x: 700 }}
        locale={{
          emptyText: (
            <div style={{ padding: '48px 20px', textAlign: 'center' }}>
              <HeartOutlined style={{ fontSize: 40, color: 'var(--wl-primary-light)', marginBottom: 12, display: 'block' }} />
              <Text style={{ color: '#666', fontSize: 14, display: 'block', marginBottom: 4 }}>No checkup recorded yet</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {isDoctor ? 'Click "New Checkup" to record the dental checkup' : 'Checkup has not been recorded for this child'}
              </Text>
            </div>
          ),
        }}
      />
    </Card>
  );

  // ── Checkup tab: form view ────────────────────────────────────
  const checkupFormView = (
    <Card
      title={
        <Space>
          <HeartOutlined style={{ color: '#ff4d4f' }} />
          <span style={{ fontFamily: "'Fredoka One', sans-serif", color: 'var(--wl-primary)' }}>
            {isEdit ? 'Update Checkup' : 'New Checkup'}
          </span>
        </Space>
      }
      extra={<Button size="small" icon={<ArrowLeftOutlined />} onClick={() => setCheckupFormVisible(false)}>Back to List</Button>}
      style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name="oralHygieneStatus" label="Oral Hygiene Status" rules={[{ required: true, message: 'Required' }]}>
              <Select placeholder="Select status">
                {Object.entries(ORAL_HYGIENE).map(([k, v]) => (
                  <Select.Option key={v} value={v}>{k.charAt(0) + k.slice(1).toLowerCase()}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item
              name="weight" label="Weight (kg)"
              validateTrigger="onBlur"
              rules={[{ validator: (_, v) => {
                if (!v && v !== 0) return Promise.resolve();
                if (!/^\d+(\.\d+)?$/.test(String(v).trim())) return Promise.reject(new Error('Enter a valid number (e.g. 10.5)'));
                return Promise.resolve();
              }}]}
            >
              <Input placeholder="e.g. 45.5" />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item
              name="height" label="Height (cm)"
              validateTrigger="onBlur"
              rules={[{ validator: (_, v) => {
                if (!v && v !== 0) return Promise.resolve();
                if (!/^\d+(\.\d+)?$/.test(String(v).trim())) return Promise.reject(new Error('Enter a valid number (e.g. 120.5)'));
                return Promise.resolve();
              }}]}
            >
              <Input placeholder="e.g. 120.5" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {[
            { name: 'hasCavities',      label: 'Cavities'        },
            { name: 'hasGumIssues',      label: 'Gum Issues'      },
            { name: 'painReported',      label: 'Pain Reported'   },
            { name: 'treatmentRequired', label: 'Needs Treatment' },
          ].map(({ name, label }) => (
            <Col xs={12} sm={6} key={name}>
              <Form.Item name={name} label={label} valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          ))}
        </Row>

        <Divider style={{ margin: '4px 0 16px' }}>Dental Assessment</Divider>

        <Row gutter={16}>
          <Col xs={12} sm={8}>
            <Form.Item name="jawDevelopment" label="Jaw Development">
              <Select placeholder="Select" allowClear>
                {Object.entries(JAW_DEVELOPMENT).map(([k, v]) => (
                  <Select.Option key={v} value={v}>
                    {k === 'TO_BE_ASSIGNED' ? 'To Be Assisted' : 'Good'}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name="upperJaw" label="Upper Jaw">
              <Select placeholder="Select" allowClear>
                {Object.entries(JAW_WIDTH).map(([k, v]) => (
                  <Select.Option key={v} value={v}>{k.charAt(0) + k.slice(1).toLowerCase()}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name="lowerArch" label="Lower Arch">
              <Select placeholder="Select" allowClear>
                {Object.entries(JAW_WIDTH).map(([k, v]) => (
                  <Select.Option key={v} value={v}>{k.charAt(0) + k.slice(1).toLowerCase()}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name="tongueMovement" label="Tongue Movement">
              <Select placeholder="Select" allowClear>
                {Object.entries(TONGUE_MOVEMENT).map(([k, v]) => (
                  <Select.Option key={v} value={v}>{k.charAt(0) + k.slice(1).toLowerCase()}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name="mouthBreathing" label="Mouth Breathing" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '4px 0 16px' }}>Treatment Details</Divider>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="treatmentType" label="Treatment Type">
              <Input placeholder="e.g. Filling, Extraction, Scaling" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="nextVisitDate" label="Next Visit">
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabledDate={(d) => d && d < dayjs().startOf('day')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="treatmentNotes" label="Treatment Notes">
              <Input.TextArea rows={3} placeholder="Treatment notes…" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="doctorRemarks" label="Doctor Remarks">
              <Input.TextArea rows={3} placeholder="Observations, remarks…" />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {isDoctor && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isSubmitting}
            onClick={handleCheckupSubmit}
            style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none', borderRadius: 9999 }}
          >
            {isEdit ? 'Update' : 'Save'}
          </Button>
          <Button onClick={() => setCheckupFormVisible(false)}>Cancel</Button>
        </div>
      )}
    </Card>
  );

  // ── Sidebar ───────────────────────────────────────────────────
  const sidebar = (
    <Card style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)', height: '100%' }} styles={{ body: { padding: 16 } }}>
      <div style={{ marginBottom: 10 }}>
        <Tooltip title={event?.title}>
          <Text strong style={{ fontSize: 14, color: 'var(--wl-primary)', display: 'block', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {event?.title || 'Event'}
          </Text>
        </Tooltip>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <Select
            placeholder="Standard"
            value={standardFilter}
            onChange={(val) => { setStandardFilter(val || 'all'); setSectionFilter('all'); }}
            allowClear={standardFilter !== 'all'}
            onClear={() => { setStandardFilter('all'); setSectionFilter('all'); }}
            clearIcon={<Tooltip title="Clear filter"><CloseCircleOutlined /></Tooltip>}
            size="small"
            style={{ width: 130 }}
          >
            <Select.Option key="all" value="all">Std: All</Select.Option>
            {STANDARD_OPTIONS.map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Section"
            value={sectionFilter}
            onChange={(val) => setSectionFilter(val || 'all')}
            allowClear={sectionFilter !== 'all'}
            onClear={() => setSectionFilter('all')}
            clearIcon={<Tooltip title="Clear filter"><CloseCircleOutlined /></Tooltip>}
            size="small"
            style={{ width: 120 }}
          >
            <Select.Option key="all" value="all">Sec: All</Select.Option>
            {sectionOptions.map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 3, background: 'var(--wl-primary-pale)', borderRadius: 10, padding: 3, marginBottom: 10 }}>
        {[
          { label: `All (${allRegistrations.length})`, value: 'all'     },
          { label: `Pending (${pendingCount})`,         value: 'pending'  },
          { label: `Done (${checkedCount})`,            value: 'checked'  },
        ].map((opt) => (
          <div
            key={opt.value}
            onClick={() => setListFilter(opt.value)}
            style={{
              flex: 1, textAlign: 'center',
              padding: '6px 4px', borderRadius: 7, cursor: 'pointer',
              background: listFilter === opt.value ? 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))' : 'transparent',
              color: listFilter === opt.value ? '#fff' : 'var(--wl-primary)',
              fontWeight: 600, fontSize: 12,
              transition: 'all 0.2s', userSelect: 'none',
            }}
          >
            {opt.label}
          </div>
        ))}
      </div>

      <Input
        prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)' }} />}
        placeholder="Search by name or mobile…"
        value={childSearch}
        onChange={(e) => setChildSearch(e.target.value)}
        allowClear
        size="small"
        style={{ borderRadius: 9999, marginBottom: 10, borderColor: 'var(--wl-primary-pale)' }}
      />
      <div style={{ maxHeight: 520, overflowY: 'auto', paddingRight: 2 }}>
        {filteredRegs.length === 0 ? (
          <Empty description="No children found" style={{ padding: 24 }} />
        ) : (
          filteredRegs.map((r) => {
            const cid        = r.child?.id || r.child?._id;
            const hasCheckup = !!checkupByChildId[String(cid || '')];
            const isActive   = (r.id || r._id) === registrationId;
            return (
              <ChildListItem
                key={r.id || r._id}
                reg={r}
                active={isActive}
                hasCheckup={hasCheckup}
                isSchoolStaff={isSchoolStaff}
                onClick={() => !isActive && navigate(`/events/${eventId}/children/${r.id || r._id}`)}
              />
            );
          })
        )}
      </div>
    </Card>
  );

  // ── Child summary card ────────────────────────────────────────
  const childSummaryCard = (
    <Card
      style={{ borderRadius: 14, border: '1.5px solid var(--wl-primary-pale)', background: 'linear-gradient(135deg, var(--wl-primary-pale) 0%, var(--wl-card-bg) 100%)' }}
      styles={{ body: { padding: '16px 20px' } }}
    >
      <Row gutter={[16, 8]} align="middle">
        <Col xs={24} sm={4} style={{ textAlign: 'center' }}>
          <Avatar
            size={56}
            src={child?.childImage ? resolveFileUrl(child.childImage) : undefined}
            style={{
              background: child?.childImage ? undefined : avatarColor(child?.name),
              fontSize: 24, fontWeight: 700, fontFamily: "'Fredoka One', sans-serif", color: '#fff',
            }}
          >
            {!child?.childImage && child?.name?.charAt(0)?.toUpperCase()}
          </Avatar>
        </Col>
        <Col xs={24} sm={14}>
          <Title level={5} style={{ margin: 0, color: 'var(--wl-text)' }}>{child?.name}</Title>
          <Space size={6} style={{ marginTop: 4 }} wrap>
            {child?.gender && (
              <Tag style={{ borderRadius: 9999, textTransform: 'capitalize', fontSize: 11 }}>{child.gender}</Tag>
            )}
            {(child?.dateOfBirth || child?.age != null) && (
              <Tag style={{ borderRadius: 9999, fontSize: 11, background: 'var(--wl-primary-pale)', color: 'var(--wl-primary)', border: '1px solid var(--wl-primary-light)', fontWeight: 600 }}>
                {child?.age != null ? `${child.age} yrs` : `${calcAge(child.dateOfBirth)} yrs`}
              </Tag>
            )}
            <Tag
              icon={isEdit ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
              color={isEdit ? 'success' : 'warning'}
              style={{ borderRadius: 9999, fontSize: 11 }}
            >
              {isEdit ? 'Checked' : 'Pending'}
            </Tag>
          </Space>
        </Col>
        <Col xs={24} sm={6}>
          <Space direction="vertical" size={2}>
            <Text style={{ fontSize: 12 }}><UserOutlined style={{ color: 'var(--wl-primary)', marginRight: 5 }} />{child?.parent?.name || '—'}</Text>
            {isSchoolStaff && (
              <Text style={{ fontSize: 12 }}><PhoneOutlined style={{ color: 'var(--wl-primary)', marginRight: 5 }} />{child?.parent?.phone || '—'}</Text>
            )}
            <Text style={{ fontSize: 12 }}><BankOutlined style={{ color: 'var(--wl-primary)', marginRight: 5 }} />{child?.school?.name || '—'}</Text>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  const NAV_TABS = [
    { key: 'checkup',        label: 'Checkup',       icon: <HeartOutlined /> },
    { key: 'smile-passport', label: 'Smile Passport', icon: <SmileOutlined /> },
    { key: 'treatment',      label: 'Treatment',      icon: <MedicineBoxOutlined /> },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 12 }}
        items={fromChild ? [
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: <span onClick={() => navigate('/children')} style={{ cursor: 'pointer', color: 'var(--wl-primary)' }}>Children</span> },
          { title: 'Child Details' },
        ] : [
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: <span onClick={() => navigate('/events')} style={{ cursor: 'pointer', color: 'var(--wl-primary)' }}>Events</span> },
          { title: <span onClick={() => navigate(`/events/${eventId}`)} style={{ cursor: 'pointer', color: 'var(--wl-primary)' }}>{event?.title}</span> },
          { title: 'Child Details' },
        ]}
      />

      {/* ── Main layout ──────────────────────────────────────────── */}
      <Row gutter={[20, 20]}>
        {!fromChild && <Col xs={24} lg={7}>{sidebar}</Col>}

        <Col xs={24} lg={fromChild ? 24 : 17}>
          {!currentReg ? (
            <Empty description="Registration not found" style={{ padding: 60 }} />
          ) : (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {childSummaryCard}

              {/* ── Tab nav ── */}
              <div style={{ display: 'flex', gap: 3, background: 'var(--wl-primary-pale)', borderRadius: 12, padding: 4 }}>
                {NAV_TABS.map((tab) => {
                  const isActive = activeTab === tab.key;
                  return (
                    <div
                      key={tab.key}
                      onClick={() => { setActiveTab(tab.key); setCheckupFormVisible(false); }}
                      style={{
                        flex: 1, textAlign: 'center',
                        padding: '9px 8px', borderRadius: 9, cursor: 'pointer',
                        background: isActive ? 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))' : 'transparent',
                        color: isActive ? '#fff' : 'var(--wl-primary)',
                        fontWeight: 600, fontSize: 13,
                        transition: 'all 0.2s',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        userSelect: 'none',
                      }}
                    >
                      {tab.icon}
                      <span>{tab.label}</span>
                    </div>
                  );
                })}
              </div>

              {/* Checkup tab */}
              {activeTab === 'checkup' && (
                checkupFormVisible ? checkupFormView : checkupListView
              )}

              {/* Smile Passport tab */}
              {activeTab === 'smile-passport' && (
                <>
                  <SmilePassportTab
                    child={child}
                    checkup={existingCheckup}
                    isDoctor={isDoctor}
                    isAdmin={isAdmin}
                    onIssue={(values, done) =>
                      issuePassportMutation.mutate(values, { onSuccess: done })
                    }
                    onUpdatePassport={(values, done) =>
                      issuePassportMutation.mutate(values, { onSuccess: done })
                    }
                    onRevoke={() => revokePassportMutation.mutate()}
                    issueLoading={issuePassportMutation.isPending}
                    revokeLoading={revokePassportMutation.isPending}
                  />

                  <Card style={{ borderRadius: 14, border: '1px solid var(--wl-primary-pale)' }} bodyStyle={{ padding: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 16 }}>
                      <div>
                        <Text strong style={{ fontSize: 15 }}>Smile Passport Records</Text>
                        <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                          Manage this child’s Smile Passport record.
                        </Text>
                      </div>
                      {existingCheckup && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setPassportEditingRecord(null);
                            setPassportModalOpen(true);
                          }}
                          disabled={!childId}
                          style={{ borderRadius: 9999, background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
                        >
                          Add Record
                        </Button>
                      )}
                    </div>
                    <Table
                      dataSource={passportRecords}
                      columns={[
                        { title: '#', width: 60, render: (_, __, index) => index + 1 },
                        { title: 'Academic Year', dataIndex: 'academic_year', width: 140 },
                        { title: 'Created Date', width: 140, render: (_, record) => record.createdAt ? dayjs(record.createdAt).format('DD MMM YYYY') : '—' },
                        { title: 'Concern', dataIndex: 'concern', render: (text) => {
                          if (!text) return '—';
                          if (text.length <= 15) return text;
                          return <Tooltip title={text}>{text.slice(0, 15)}...</Tooltip>;
                        }},
                        {
                          title: 'Status',
                          width: 110,
                          render: (_, r) => (
                            <Tag color={r.status === 'published' ? 'success' : 'default'} style={{ textTransform: 'capitalize' }}>
                              {r.status || 'draft'}
                            </Tag>
                          ),
                        },
                        {
                          title: 'Actions',
                          key: 'actions',
                          width: 180,
                          align: 'center',
                          render: (_, record) => (
                            <Space size={6}>
                              <Tooltip title="Preview PDF">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<FilePdfOutlined />}
                                  onClick={() => {
                                    setPassportPreviewRecord(record);
                                    setPassportPreviewOpen(true);
                                  }}
                                />
                              </Tooltip>
                              {(
                                <>
                                  <Tooltip title="Edit Record">
                                    <Button
                                      shape="circle"
                                      size="small"
                                      icon={<EditOutlined />}
                                      style={{ background: '#ffffff', border: '1px solid var(--wl-primary)', color: 'var(--wl-primary)' }}
                                      onClick={() => {
                                        setPassportEditingRecord(record);
                                        setPassportModalOpen(true);
                                      }}
                                    />
                                  </Tooltip>
                                  <Popconfirm
                                    title="Delete this record?"
                                    onConfirm={() => deletePassportRecordMutation.mutate(record.id || record._id)}
                                    okText="Delete"
                                    okType="danger"
                                    cancelText="Cancel"
                                  >
                                    <Tooltip title="Delete Record">
                                      <Button
                                        shape="circle"
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        loading={deletePassportRecordMutation.isPending}
                                        style={{ background: '#fff2f0', border: '1px solid #ff4d4f', color: '#ff4d4f' }}
                                      />
                                    </Tooltip>
                                  </Popconfirm>
                                </>
                              )}
                            </Space>
                          ),
                        },
                      ]}
                      rowKey={(record) => record.id || record._id}
                      loading={passportLoading}
                      pagination={false}
                      locale={{
                        emptyText: (
                          <div style={{ padding: 40, textAlign: 'center' }}>
                            <SmileOutlined style={{ fontSize: 32, color: 'var(--wl-primary-light)', marginBottom: 12 }} />
                            <div style={{ color: '#666', fontSize: 14 }}>No Smile Passport records yet.</div>
                            <div style={{ color: '#999', fontSize: 12 }}>Click Add Record to create one.</div>
                          </div>
                        ),
                      }}
                    />
                  </Card>
                </>
              )}

              {/* Treatment tab */}
              {activeTab === 'treatment' && (
                <TreatmentDetailsTab
                  checkup={existingCheckup}
                  isDoctor={isDoctor}
                  childId={childId}
                  treatmentRecords={treatmentRecords}
                  treatmentLoading={treatmentLoading}
                  onAddTreatment={() => {
                    setTreatmentEditingRecord(null);
                    setTreatmentModalOpen(true);
                  }}
                  onEditTreatment={(record) => {
                    setTreatmentEditingRecord(record);
                    setTreatmentModalOpen(true);
                  }}
                  onDeleteTreatment={(id) => deleteTreatmentMutation.mutate(id)}
                  deleteLoading={deleteTreatmentMutation.isPending}
                  onPreviewReport={(record) => {
                    setReportPreviewRecord(record);
                    setReportPreviewOpen(true);
                  }}
                />
              )}
            </Space>
          )}
        </Col>
      </Row>
      <SmilepassportModal
        open={passportModalOpen}
        onClose={() => {
          setPassportModalOpen(false);
          setPassportEditingRecord(null);
        }}
        eventId={eventId}
        childId={childId}
        childName={child?.name}
        editingRecord={passportEditingRecord}
        onSuccess={() => {
          queryClient.invalidateQueries(['event-smilepassports', eventId, childId]);
          setPassportModalOpen(false);
          setPassportEditingRecord(null);
        }}
      />
      <SmilepassportPreviewModal
        open={passportPreviewOpen}
        onClose={() => {
          setPassportPreviewOpen(false);
          setPassportPreviewRecord(null);
        }}
        record={passportPreviewRecord}
        child={child}
      />
      <TreatmentModal
        open={treatmentModalOpen}
        onClose={() => {
          setTreatmentModalOpen(false);
          setTreatmentEditingRecord(null);
        }}
        eventId={eventId}
        childId={childId}
        childName={child?.name}
        eventName={event?.title}
        defaultClinic={event?.clinic?.id || event?.clinic?._id || event?.clinic}
        editingRecord={treatmentEditingRecord}
        onSuccess={() => {
          queryClient.invalidateQueries(['event-treatments', eventId, childId]);
          setTreatmentModalOpen(false);
          setTreatmentEditingRecord(null);
        }}
      />
      <TreatmentReportPreviewModal
        open={reportPreviewOpen}
        onClose={() => {
          setReportPreviewOpen(false);
          setReportPreviewRecord(null);
        }}
        record={reportPreviewRecord}
      />
    </div>
  );
};

export default ChildDetailsPage;
